/**
 * Estado de la partida en curso para la interfaz.
 *
 * Reparto de responsabilidades: el motor decide *qué pasa* y el store decide
 * *cómo se enseña*. Aquí no hay ni una regla de juego; lo único que añade sobre
 * el motor es la reproducción temporizada de la fase de revelación y el estado
 * puramente visual (carta seleccionada, aviso de jugada ilegal).
 *
 * Por eso `endTurn` no aplica el estado final de golpe: recorre las
 * instantáneas que devuelve el motor con una pausa entre cada una, que es lo que
 * convierte "se han revelado 4 cartas" en algo que se puede seguir con la vista.
 */

import { create } from 'zustand';
import { STARTER_DECK_IDS } from '@/data';
import { runOpponentTurn } from '@/engine/aiOpponent';
import {
  createMatch,
  playCard,
  recallCard,
  resolveTurn,
  type DeckEntry,
} from '@/engine/combatEngine';
import type { GameState, RevealStep, ZoneIndex } from '@/types/game';

/**
 * Pausa entre revelaciones, adaptada a cuántas cartas se destapan.
 *
 * Un tiempo fijo funciona con 2 o 3 cartas y se hace eterno con 8: en los turnos
 * finales de una partida larga ambos bandos vuelcan varias cartas y la espera se
 * multiplica. Así el turno entero dura más o menos lo mismo se revele lo que se
 * revele, y las revelaciones cortas —las que suelen decidir algo— se ven
 * despacio.
 */
const REVEAL_BUDGET_MS = 3200;
const REVEAL_STEP_MIN_MS = 260;
const REVEAL_STEP_MAX_MS = 750;

function revealStepDelay(stepCount: number): number {
  if (stepCount <= 0) return REVEAL_STEP_MAX_MS;
  const perStep = REVEAL_BUDGET_MS / stepCount;
  return Math.max(REVEAL_STEP_MIN_MS, Math.min(REVEAL_STEP_MAX_MS, perStep));
}
/** Respiro tras pulsar "Terminar turno", antes de que se destape la primera carta. */
const REVEAL_LEAD_IN_MS = 350;
/** Cuánto se queda en pantalla el aviso de jugada ilegal. */
const NOTICE_MS = 2600;

const GUIDE_STORAGE_KEY = 'triada.guia-vista';

/** Si ya se vio la guia. Tolera que no haya almacenamiento disponible. */
function readGuideDismissed(): boolean {
  try {
    return localStorage.getItem(GUIDE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Una jugada registrada para poder demostrarla después.
 *
 * En PvP el servidor no acepta "he ganado": acepta la lista de jugadas y vuelve
 * a simular la partida con ellas. Así que hay que ir apuntándolas según se
 * hacen, incluidas las retiradas — una carta retirada no llegó a jugarse y no
 * puede aparecer en el registro.
 */
export interface RecordedMove {
  turn: number;
  instanceId: string;
  zone: ZoneIndex;
}

/** El rival de un duelo: su mazo es una instantánea, no una referencia. */
export interface PvpOpponent {
  userId: string;
  username: string;
  deck: DeckEntry[];
}

export interface NewMatchOptions {
  /** Semilla fija para reproducir una partida concreta (tests, repeticiones). */
  seed?: number;
  /**
   * Mazo del jugador: id de carta, o carta y nivel si está fusionada. Sin esto
   * se usa el mazo inicial, que es lo que corresponde en modo local.
   */
  deckIds?: readonly DeckEntry[];
  /** Rival humano. Sin esto la partida es contra la IA y no se envía a ningún sitio. */
  opponent?: PvpOpponent;
}

interface GameStore {
  /** null hasta que se reparte la primera partida. */
  game: GameState | null;

  /** Carta de la mano marcada para jugar tocando una zona (flujo táctil). */
  selectedCardId: string | null;
  /** Carta que el jugador está arrastrando ahora mismo (flujo de ratón/dedo). */
  draggingCardId: string | null;
  /** Aviso efímero: "te faltan 2 de energía". */
  notice: string | null;

  /** Pasos del turno que se está revelando, para resaltar la carta activa. */
  revealSteps: RevealStep[];
  revealIndex: number;
  /** Bloquea la entrada mientras corre la animación. */
  isResolving: boolean;

  /** Rival humano, si la partida es un duelo. */
  pvpOpponent: PvpOpponent | null;
  /** Semilla de la partida en curso: el servidor la necesita para re-simularla. */
  pvpSeed: number | null;
  /** Jugadas hechas, en orden, para que el servidor pueda validarlas. */
  moves: RecordedMove[];

  /** Carta que se está mirando ampliada. Null = ninguna. */
  inspectedCardId: string | null;
  /** El jugador ha pedido consejo para este turno. */
  hintVisible: boolean;
  /**
   * Ya se ha visto la guia de la primera partida.
   *
   * Vive aqui y no como consulta directa a localStorage porque es estado de
   * interfaz como cualquier otro: asi se puede fijar en una prueba sin depender
   * de que el navegador tenga almacenamiento, y la persistencia queda reducida a
   * una sola funcion.
   */
  guideDismissed: boolean;

  newMatch: (options?: NewMatchOptions) => void;
  inspectCard: (instanceId: string | null) => void;
  toggleHint: () => void;
  dismissGuide: () => void;
  selectCard: (instanceId: string | null) => void;
  setDragging: (instanceId: string | null) => void;
  playSelectedInto: (zone: ZoneIndex) => void;
  playCardInto: (instanceId: string, zone: ZoneIndex) => void;
  recall: (instanceId: string) => void;
  endTurn: () => Promise<void>;
  showNotice: (message: string) => void;
}

let noticeTimer: ReturnType<typeof setTimeout> | undefined;

export const useGameStore = create<GameStore>((set, get) => ({
  game: null,
  selectedCardId: null,
  draggingCardId: null,
  notice: null,
  revealSteps: [],
  revealIndex: -1,
  isResolving: false,
  pvpOpponent: null,
  pvpSeed: null,
  moves: [],
  inspectedCardId: null,
  hintVisible: false,
  guideDismissed: readGuideDismissed(),

  newMatch: (options) => {
    const playerDeck = options?.deckIds?.length ? options.deckIds : STARTER_DECK_IDS;
    // La semilla se fija aquí aunque no venga dada: en un duelo hay que
    // enviársela al servidor para que reproduzca exactamente esta partida.
    const seed = options?.seed ?? Math.floor(Math.random() * 0xffffffff);
    set({
      game: createMatch({
        playerDeckIds: playerDeck,
        // Contra la IA, el bot juega con el mazo inicial: enfrentarlo a la lista
        // propia haría que cada mejora de mazo se anulase sola. En un duelo,
        // juega el mazo real del rival.
        opponentDeckIds: options?.opponent?.deck ?? STARTER_DECK_IDS,
        opponentName: options?.opponent?.username,
        seed,
      }),
      pvpOpponent: options?.opponent ?? null,
      pvpSeed: seed,
      moves: [],
      inspectedCardId: null,
      hintVisible: false,
      selectedCardId: null,
      draggingCardId: null,
      notice: null,
      revealSteps: [],
      revealIndex: -1,
      isResolving: false,
    });
  },

  inspectCard: (instanceId) => set({ inspectedCardId: instanceId }),

  dismissGuide: () => {
    set({ guideDismissed: true });
    try {
      localStorage.setItem(GUIDE_STORAGE_KEY, '1');
    } catch {
      // Sin almacenamiento (modo privado, WebView restringido) la guia volvera
      // a salir. Es el fallo en la direccion correcta: mostrar de mas unas
      // instrucciones molesta; perderlas deja a alguien sin saber jugar.
    }
  },

  toggleHint: () => set((prev) => ({ hintVisible: !prev.hintVisible })),

  selectCard: (instanceId) => {
    if (get().isResolving) return;
    // Volver a tocar la carta ya marcada la deselecciona.
    set((prev) => ({
      selectedCardId: prev.selectedCardId === instanceId ? null : instanceId,
    }));
  },

  setDragging: (instanceId) => set({ draggingCardId: instanceId }),

  showNotice: (message) => {
    clearTimeout(noticeTimer);
    set({ notice: message });
    noticeTimer = setTimeout(() => set({ notice: null }), NOTICE_MS);
  },

  playSelectedInto: (zone) => {
    const selected = get().selectedCardId;
    if (!selected) return;
    get().playCardInto(selected, zone);
  },

  playCardInto: (instanceId, zone) => {
    const { game, isResolving } = get();
    if (!game || isResolving) return;

    const result = playCard(game, 'PLAYER', instanceId, zone);
    if (!result.ok) {
      get().showNotice(result.error);
      return;
    }
    set((prev) => ({
      game: result.state,
      selectedCardId: null,
      draggingCardId: null,
      moves: [...prev.moves, { turn: game.turn, instanceId, zone }],
    }));
  },

  recall: (instanceId) => {
    const { game, isResolving } = get();
    if (!game || isResolving) return;

    const result = recallCard(game, 'PLAYER', instanceId);
    if (!result.ok) {
      get().showNotice(result.error);
      return;
    }
    // La jugada se borra del registro: una carta retirada no llegó a jugarse, y
    // si apareciera en la lista el servidor rechazaría la partida entera por
    // considerarla ilegal.
    set((prev) => ({
      game: result.state,
      selectedCardId: null,
      moves: prev.moves.filter(
        (move) => !(move.instanceId === instanceId && move.turn === game.turn),
      ),
    }));
  },

  endTurn: async () => {
    const { game, isResolving } = get();
    if (!game || isResolving || game.phase !== 'PLANNING') return;

    // El consejo caduca con el turno: mantenerlo visible durante la
    // revelación señalaría una jugada que ya no se puede hacer.
    set({ isResolving: true, selectedCardId: null, notice: null, hintVisible: false });

    // El bot coloca sus cartas en paralelo, igual que el jugador: sus cartas
    // quedan boca abajo y no se sabrá qué son hasta la revelación.
    const { state: staged } = runOpponentTurn(game);
    const { steps, finalState } = resolveTurn(staged);

    set({ game: staged, revealSteps: steps, revealIndex: -1 });
    await sleep(REVEAL_LEAD_IN_MS);

    const stepDelay = revealStepDelay(steps.length);
    for (let i = 0; i < steps.length; i += 1) {
      const step = steps[i];
      if (!step) continue;
      set({ game: step.stateAfter, revealIndex: i });
      await sleep(stepDelay);
    }

    set({ game: finalState, revealIndex: -1, revealSteps: [], isResolving: false });
  },
}));
